import apiFetch from '../../../utils/apiFetch';

/** The repeat-save identity the server checks before a turn can end (see POST /combat/end-turn). */
export const repeatSaveRequestId = (effectId, combatantId, turnSequence) => `repeat-save:${effectId}:${combatantId}:${turnSequence}`;

/**
 * Ends the active turn through the server command (audit P1/P4). The server advances its stored combat, so the
 * client never sends a timeline. The request id is fixed per combatant and turn, so a retry replays instead of
 * advancing twice. When the server lists unresolved end-of-turn repeat saves, `resolvePendingSave` rolls and commits
 * each one (DM screen or the player's sheet), and the same request is sent again. Returns the committed combat.
 */
export const requestServerEndTurn = async ({ campaignId, combat, resolvePendingSave }) => {
  const turnSequence = combat?.turnSequence;
  const active = combat?.participants?.[combat?.activeTurn];
  const combatantId = String(active?.characterId || active?.enemyId || '');
  if (!campaignId || !Number.isInteger(turnSequence) || !combatantId) throw new Error('No turn is active.');
  const requestId = `end-turn:${combatantId}:${turnSequence}`;
  const send = async () => {
    const response = await apiFetch(`/campaigns/${encodeURIComponent(campaignId)}/combat/end-turn`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ expectedTurnSequence: turnSequence, requestId }),
    });
    const body = await response.json().catch(() => ({}));
    return { response, body };
  };
  let { response, body } = await send();
  if (response.status === 409 && body.status === 'end-of-turn-saves-pending' && Array.isArray(body.saves)) {
    if (typeof resolvePendingSave !== 'function') throw new Error(body.message || 'Resolve the end-of-turn saving throws first.');
    for (const save of body.saves) {
      const id = repeatSaveRequestId(save.effectId, combatantId, turnSequence);
      // Sequential: each save is its own roll and commit. An effect without a recorded source is treated as the
      // ending combatant's own (the save resolver needs one).
      // eslint-disable-next-line no-await-in-loop
      await resolvePendingSave({ ...save, sourceCombatantId: save.sourceCombatantId || combatantId, requestId: id, replayIdentity: id, targetCombatantId: combatantId, turnSequence });
    }
    ({ response, body } = await send());
  }
  if (!response.ok) throw Object.assign(new Error(body.message || body.errors?.[0]?.msg || 'The turn could not be ended.'), { status: response.status, body });
  return body;
};
